import { Link } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader";

const Section = ({ id, title, children }) => (
  <section className="mb-10" data-testid={`policy-${id}`} id={id}>
    <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mb-3">
      {title}
    </h2>
    <div className="text-slate-700 text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function PrivacyPage() {
  const updated = "January 2026";
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader />
      <main
        data-testid="privacy-main"
        className="max-w-3xl mx-auto px-6 py-12 sm:py-16 flex-1 w-full"
      >
        <p className="eyebrow mb-2">Legal</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-10">Last updated: {updated}</p>

        <Section id="introduktion" title="1. About this policy">
          <p>
            This privacy policy describes how <strong>Awarak AB</strong> (&ldquo;<strong>we</strong>&rdquo;,
            &ldquo;<strong>us</strong>&rdquo;), operating the SwingShape service, processes
            personal data when you use our AI-powered golf swing analysis. We have designed
            the service around the principle of <strong>data minimisation</strong> &mdash; we
            collect as little information as possible and delete anything we are not required
            to keep.
          </p>
        </Section>

        <Section id="personuppgiftsansvarig" title="2. Data controller">
          <p>
            <strong>Awarak AB</strong> is the data controller for the processing of your personal
            data on{" "}
            <a className="text-[#065F46] underline" href="https://swingshape.se">
              swingshape.se
            </a>
            . SwingShape is operated by Awarak AB; Stripe payments are processed through
            Awarak AB&apos;s Stripe account and bank account.
          </p>
          <p className="text-slate-500 text-sm">
            [Awarak AB organisation number to be added here.]
          </p>
          <div
            data-testid="gdpr-contact-block"
            className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4"
          >
            <p className="text-[15px] text-slate-800 leading-relaxed">
              For questions regarding privacy, personal data, data deletion requests,
              or GDPR rights, please contact us at:{" "}
              <a
                className="font-semibold text-[#065F46] underline break-all hover:no-underline"
                href="mailto:support@swingshape.se"
                data-testid="gdpr-contact-email"
              >
                support@swingshape.se
              </a>
            </p>
          </div>
        </Section>

        <Section id="behandlade-uppgifter" title="3. What data we process and why">
          <p>
            To deliver a swing analysis (performance of our contract with you under{" "}
            <em>Article 6(1)(b) GDPR</em>) we temporarily process two pieces of information:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Your uploaded golf swing video</strong> &mdash; used solely so the AI can
              analyse your swing technique.
            </li>
            <li>
              <strong>The problem you describe in text</strong> &mdash; used solely so the AI can
              focus its feedback on what you actually want help with.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> collect your name, email address, phone number, or any
            other directly identifying information on our side. There are no user accounts.
          </p>
          <p>
            <strong>Note on IP addresses.</strong> We never log your IP address in our
            application or database. However, like any internet service, our hosting provider
            and content-delivery network may briefly observe your IP address at the network
            edge for security and abuse prevention. Such edge logs are typically retained for
            up to 72 hours by the provider and are not accessible to us as personally
            identifiable data.
          </p>
        </Section>

        <Section id="lagring" title="4. Storage and retention">
          <p>
            We apply a strict short-retention policy:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>The video file</strong> is permanently deleted from the server the moment
              OpenAI&apos;s API has returned its text response (typically within 30 seconds of
              upload). The video is never written to disk or object storage.
            </li>
            <li>
              <strong>Your problem description and the AI response</strong> are stored together
              with the submission for up to <strong>24 hours</strong>, so that you can safely
              refresh the page, switch tabs, briefly lose connection, or complete Stripe
              checkout without losing your analysis. After 24 hours the entire submission is
              automatically and permanently deleted by our database. You are welcome to copy
              the analysis or download it as a PDF from the results page to keep a copy.
            </li>
          </ul>
          <p>
            The only thing we keep in the database beyond 24 hours is a Stripe transaction id
            and a timestamp, which is required for accounting and for handling any refunds
            (see section 6).
          </p>
        </Section>

        <Section id="tredje-part" title="5. Third-party processors">
          <p>To deliver the service we rely on the following sub-processors:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>OpenAI (USA)</strong> &mdash; receives still frames from your video together
              with your problem description in order to generate the analysis. Data is
              transferred over an encrypted HTTPS API. OpenAI does not train its models on API
              inputs.
            </li>
            <li>
              <strong>Stripe (Ireland / USA)</strong> &mdash; processes your payment. When you
              proceed to checkout you are redirected to a page hosted by Stripe where you enter
              your <em>card number, billing address, country, and email</em> directly into
              Stripe. <strong>That information is collected by Stripe, not by us</strong>; we
              never see or store it. Stripe acts as an independent data controller for those
              payment details and processes them under its own{" "}
              <a
                className="text-[#065F46] underline"
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </li>
            <li>
              <strong>Hosting provider</strong> &mdash; runs the application servers and the
              database. Edge logs may temporarily capture your IP address (see section 3).
            </li>
          </ul>
          <p>
            All communication with these parties is encrypted in transit (HTTPS/TLS). Any
            international transfers rely on the EU&ndash;US Data Privacy Framework and/or the
            EU&apos;s Standard Contractual Clauses.
          </p>
        </Section>

        <Section id="betalningsuppgifter" title="6. What we keep for accounting">
          <p>
            To comply with Swedish bookkeeping law and to be able to handle refunds we store{" "}
            <strong>only the following</strong> per completed transaction:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Stripe transaction id</strong> (an opaque reference &mdash; not your
              identity)
            </li>
            <li>Timestamp</li>
            <li>Amount and currency</li>
            <li>Status (success / refunded)</li>
          </ul>
          <p>
            On our side this data contains <strong>no directly identifying personal data</strong>.
            The card details themselves remain encrypted with Stripe, not with us. Bookkeeping
            data is retained for seven years as required by Swedish law (
            <em>Bokföringslagen 7 kap. 2 §</em>).
          </p>
        </Section>

        <Section id="rattslig-grund" title="7. Legal basis">
          <p>We process your data on the following GDPR legal bases:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Contract (Art. 6(1)(b))</strong> &mdash; to deliver the analysis you
              ordered and process your payment.
            </li>
            <li>
              <strong>Consent (Art. 6(1)(a))</strong> &mdash; your explicit tick of the &ldquo;By
              starting the analysis you agree...&rdquo; checkbox before the analysis is started.
            </li>
            <li>
              <strong>Legal obligation (Art. 6(1)(c))</strong> &mdash; to keep accounting records
              for the period required by Swedish law.
            </li>
          </ul>
        </Section>

        <Section id="sakerhet" title="8. Security and storage technologies">
          <p>
            All traffic between your device and our servers is encrypted with HTTPS/TLS. API
            keys and other secrets are stored in private environment variables and are never
            exposed in client code. The database runs on managed infrastructure with
            encryption-at-rest.
          </p>
          <p>
            <strong>Cookies and similar technologies.</strong> The public SwingShape site
            does <strong>not</strong> set any tracking, advertising or analytics cookies. The
            only local-storage entries we use are (a) your consent choice if applicable, and
            (b) an authentication token used exclusively by SwingShape staff to access the
            internal admin dashboard.
          </p>
          <p>
            <strong>Data breach notification.</strong> In the event of a personal-data breach
            that is likely to result in a risk to your rights and freedoms, we will notify
            the Swedish Authority for Privacy Protection (IMY) within 72 hours of becoming
            aware of the breach, in accordance with Article 33 GDPR, and we will inform
            affected individuals where Article 34 GDPR requires it.
          </p>
          <p>
            <strong>Automated decision-making.</strong> The AI feedback we generate does not
            produce any decision that has a legal or similarly significant effect on you
            within the meaning of Article 22 GDPR. It is educational coaching content only.
          </p>
        </Section>

        <Section id="barn" title="8a. Age requirement">
          <p>
            SwingShape is not directed at children. By using the service you confirm that
            you are at least 16 years old, or that you have the consent of a parent or
            guardian. If you believe a child has used the service, please contact us at{" "}
            <a className="text-[#065F46] underline" href="mailto:support@swingshape.se">
              support@swingshape.se
            </a>{" "}
            and we will delete any related data.
          </p>
        </Section>

        <Section id="dina-rattigheter" title="9. Your rights">
          <p>Under GDPR you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>request information about what data we hold about you (subject access &mdash; Art. 15),</li>
            <li>have inaccurate data corrected (Art. 16),</li>
            <li>have your data deleted (the &ldquo;right to be forgotten&rdquo; &mdash; Art. 17),</li>
            <li>restrict or object to processing (Art. 18 / 21),</li>
            <li>receive your data in a portable machine-readable format (Art. 20),</li>
            <li>withdraw any consent you previously gave (Art. 7),</li>
            <li>
              lodge a complaint with the{" "}
              <a
                className="text-[#065F46] underline"
                href="https://www.imy.se/en/"
                target="_blank"
                rel="noreferrer"
              >
                Swedish Authority for Privacy Protection (IMY)
              </a>{" "}
              if you believe we are processing your data unlawfully.
            </li>
          </ul>
          <p>
            Because we deliberately do not store any directly identifying information about you,
            in most cases we cannot link a request back to a specific person. If you need us to
            delete a transaction id from our accounting records, send the relevant Stripe id
            from your payment confirmation to{" "}
            <a className="text-[#065F46] underline" href="mailto:support@swingshape.se">
              support@swingshape.se
            </a>{" "}
            and we will help you (subject to the bookkeeping obligations we are required to
            meet under Swedish law).
          </p>
        </Section>

        <Section id="andringar" title="10. Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be announced on
            the home page and the &ldquo;Last updated&rdquo; date above always reflects the
            current version.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-slate-200 text-sm">
          <Link
            to="/"
            data-testid="back-to-home-link"
            className="text-[#065F46] hover:underline"
          >
            &larr; Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}
